import { queryOptions, useQuery } from '@tanstack/react-query';
import { fetchAdminPlatformFeatures } from '@/api/(admin)/platform-features';
import type { PlatformFeatureRow } from '../-types';

/** Everything under this prefix — what the write paths invalidate. */
export const adminPlatformFeaturesQueryKey = [
  'admin',
  'platform-features',
] as const;

/**
 * Shared by the route loader and the page, so the prefetch warms what the page
 * reads.
 *
 * No parameters: `PlatformFeaturesListQuerySchema` accepts only `companyId`,
 * and this screen is not scoped to a company. The endpoint therefore returns
 * every feature in one response — which is why the table sorts and filters
 * client-side and has no pagination.
 */
export function adminPlatformFeaturesQueryOptions() {
  return queryOptions({
    queryKey: [...adminPlatformFeaturesQueryKey, 'list'] as const,
    queryFn: () => fetchAdminPlatformFeatures(''),
  });
}

export function useAdminPlatformFeatures() {
  const query = useQuery(adminPlatformFeaturesQueryOptions());

  const rows: PlatformFeatureRow[] = (query.data?.data ?? []).map(row => ({
    ...row,
    // The unscoped list always computes it; the shared type marks it optional
    // because the company-scoped call does not.
    enabledCompaniesCount: row.enabledCompaniesCount ?? 0,
  }));

  return { ...query, rows };
}
